import { FetchAPI, FieldAPI, FormAPI, LayoutAPI, PanelAPI} from '@philips-internal/oswald-ui-components';
import Controller, { OnBeforeFetch, OnBeforeSubmit, OnBlur, OnEdit, OnMount} from '@philips-internal/oswald-ui-core/lib/client';
import ProfileBaseController from '../profile-base-controller';
import { EditEvent, FetchEvent, MountEvent} from '@philips-internal/oswald-ui-core/lib/component/types';
import { AlternativeLoginAliases } from '../../codegen/constants/aliases/alternative-login-aliases';
import { BrokerService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { AbortableSubmitEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { BS_ALTERNATIVE_LOGIN_VISIBILITY, USERNAME_MIN_LENGTH_VALIDATION } from '../../constants/application-constants';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import { showToastNotificationMessage } from '../../utils/toast-notification-utils';
import { HttpResponse, HttpService } from '@philips-internal/oswald-ui-core/lib/services/http-service';
import { ToastResponse } from '../../interfaces/ToastResponse';
import { Inject } from '@odinjs/odin';
import { Value } from '@philips-internal/oswald-ui-core';
import { HttpServiceError, PatientHttpService } from '../../services/patient-http-service';
import UserContext from '../../utils/user-context';
import { FragmentNames } from '../../codegen/constants';

interface Target {
  state: State;
}

interface FieldTypes {
  dsValorNew: Value;
  dsValorOld: Value;
  nmAtributo: string;
}

interface State {
  bodyToUpdate: Record<string, Value>;
  oldValues: Record<string, FieldsValue>;
  record: Record<string, FieldsValue>;
}

interface FieldsValue {
  alternativeLogin: string;
  replacementLogin: string;
}

@Controller({ fragment: FragmentNames.ALTERNATIVE_LOGIN })
export default class AlternativeLoginController extends ProfileBaseController {

  @Inject(AlternativeLoginAliases.ALTERNATIVE_LOGIN_FETCH)
    fecthAlternativeLoginAPI!: FetchAPI;

  @Inject(AlternativeLoginAliases.REPLACEMENT_LOGIN_FIELD)
    fieldReplacementLoginAPI!: FieldAPI;

  @Inject(AlternativeLoginAliases.ALTERNATIVE_LOGIN_FIELD)
    fieldAlternativeLoginAPI!: FieldAPI;

  @Inject(AlternativeLoginAliases.ALTERNATIVE_LOGIN_FORM)
    altLoginFormAPI!: FormAPI;

  @Inject(AlternativeLoginAliases.LAYOUT_ALTERNATIVE_LOGIN)
    alternativeLoginLayoutAPI!: LayoutAPI;

  @Inject(AlternativeLoginAliases.PANEL_ALTERNATIVE_LOGIN)
    alternativeLoginPanelAPI!: PanelAPI;

  @OnMount(AlternativeLoginAliases.ALTERNATIVE_LOGIN_FORM)
  onMountHealthForm(event: MountEvent): void {
    const formApi = event.target as FormAPI;
    super.setContextVariablesForTaskModule();
    super.listenWhetherProfileEditingIsEnabled(formApi);
  }

  @OnEdit({target: AlternativeLoginAliases.ALTERNATIVE_LOGIN_FORM})
  onEdit(event: EditEvent): void {
    const target = event.target as unknown as Target;
    this.context.set({
      oldValues : target.state.record || { undefined }
    })
  }

  @OnBeforeFetch({ target: AlternativeLoginAliases.ALTERNATIVE_LOGIN_FETCH})
  handleBeforeFetchContactInformation(event: FetchEvent): void {
    const fetchAlternativeLogin = event.target as FetchAPI;
    fetchAlternativeLogin.abort();
  }

  @OnMount({ target: AlternativeLoginAliases.PANEL_ALTERNATIVE_LOGIN})
  handleMountPanelAlternativeLogin(event: MountEvent): void {
    const alternativeLoginPanelAPI = event.target as PanelAPI;

    HttpService.get(APPLICATION_ENDPOINTS.GET_ALTERNATIVE_LOGIN, { patientCode: UserContext.patientId })
      .then((response) => {
        const body = response.body as Record<string, Value>;
        this.altLoginFormAPI.setData(body);
      }).catch(() => {
        alternativeLoginPanelAPI.setVisible(false);
        this.notifyCardAlternativeLoginVisibilityChanged(false);
      }).finally(() => { this.altLoginFormAPI.setLoading(false) });
  }

  private notifyCardAlternativeLoginVisibilityChanged(isCardAlternativeLoginVisible: boolean): void {
    BrokerService.publishToTopic(BS_ALTERNATIVE_LOGIN_VISIBILITY, isCardAlternativeLoginVisible);
  }

  @OnBeforeSubmit({target: AlternativeLoginAliases.ALTERNATIVE_LOGIN_FORM})
  async onSubmitAlternativeLogin(event: AbortableSubmitEvent): Promise<void> {

    const fields: FieldTypes[] = [];
    const insertVal: Record<string, Value>[] = [];
    const newValues = event.record as Record<string, FieldsValue>;
    const oldValues = this.context.get('oldValues') as Record<string, Value>;
    const body = { fields, insertVal };
    const alternativeLogin = this.fieldAlternativeLoginAPI.getValue();
    const replacementLogin = this.fieldReplacementLoginAPI.getValue();

    await this.validateAlternativeLoginFields(alternativeLogin, oldValues);
    await this.validadeReplacementField(replacementLogin, oldValues);

    if (!this.fieldAlternativeLoginAPI.validate() || !this.fieldReplacementLoginAPI.validate()) {
      event.abort();
      return;
    }

    Object.keys(newValues).forEach(key => {
      if(oldValues && oldValues[key] !== newValues[key]) {
        const oldV = oldValues[key] as unknown as Record<string, Value>;
        const newV = newValues[key] as unknown as Record<string, Value>;
        body.fields.push({
          dsValorNew: newV ? newV.alternativeLogin || newV.replacementLogin || newV : '',
          dsValorOld: oldV ? oldV.alternativeLogin || oldV.replacementLogin || oldV : '',
          nmAtributo: key,
        });
      }
    });

    if(body.fields.length === 0 ){
      return ;
    }

    this.sendUpdateRequest(body);
  }

  sendUpdateRequest(body: Record<string, Value>): void {
    this.altLoginFormAPI.setLoading(true);
    PatientHttpService.post<ToastResponse>(APPLICATION_ENDPOINTS.POST_ALTERNATIVE_LOGIN, {patientId: UserContext.patientId}, { body })
      .then((response) => {
        this.handleResponse(response);
        this.fecthAlternativeLoginAPI.forceFetch();
      }).catch(({ response }: HttpServiceError<ToastResponse>) => {
        this.handleResponse(response);
      }).finally(() =>{this.altLoginFormAPI.setLoading(false)});
  }

  handleResponse(resp?: HttpResponse<ToastResponse>): void {
    if (resp?.body) {
      showToastNotificationMessage(resp.body);
    }
  }

  @OnMount(AlternativeLoginAliases.ALTERNATIVE_LOGIN_FIELD)
  handleFieldLoginLoading(event: MountEvent): void {
    const usernameFieldAPI = event.target as FieldAPI;
    usernameFieldAPI.setHelperText(MessageService.translate('patient.input.min-length', { minCaracter: USERNAME_MIN_LENGTH_VALIDATION }));
  }

  @OnBlur(AlternativeLoginAliases.ALTERNATIVE_LOGIN_FIELD)
  onBlurAlternativeLogin(): void {
    const alternativeLogin = this.altLoginFormAPI.getRecord().alternativeLogin;
    const oldValues = this.context.get('oldValues') as Record<string, Value>;
    this.validateAlternativeLoginFields(alternativeLogin, oldValues);
  }

  validateAlternativeLoginFields(alternativeLogin: Value, oldValues: Record<string, Value>): Promise<void> {
    if (alternativeLogin !== oldValues.alternativeLogin){
      PatientHttpService.post(
        APPLICATION_ENDPOINTS.NEW_ACCOUNT_CHECK_USERNAME, {},
        { body: { username: alternativeLogin } }
      ).then(() => {
        return Promise.resolve(this.removeAlternativeValidator());
      })
        .catch(() => {
          return Promise.resolve(this.addAlternativeValidator());
        });
    }
    return Promise.resolve(this.removeAlternativeValidator());
  }

  addAlternativeValidator():void {
    this.fieldAlternativeLoginAPI.addLocalValidator('username-already-exist-local-validator', () => {
      return [{
        message: MessageService.translate('patient.username.already.exist'),
        name: 'patient.username.already.exist',
        valid: false
      }];
    });
    this.fieldAlternativeLoginAPI.validate();
  }

  removeAlternativeValidator(): void {
    this.fieldAlternativeLoginAPI.removeLocalValidator('username-already-exist-local-validator');
    this.fieldAlternativeLoginAPI.validate();
  }

  @OnBlur(AlternativeLoginAliases.REPLACEMENT_LOGIN_FIELD)
  onBlurReplacementLogin(): void {
    const replacementLogin = this.altLoginFormAPI.getRecord().replacementLogin;
    const oldValues = this.context.get('oldValues') as Record<string, Value>;
    this.validadeReplacementField(replacementLogin, oldValues);
  }

  validadeReplacementField(replacementLogin: Value, oldValues: Record<string, Value>): Promise<void> {
    if (replacementLogin !== oldValues.replacementLogin){
      PatientHttpService.post(APPLICATION_ENDPOINTS.CHECK_REPLACEMENT_LOGIN, {},
        { body: { username: replacementLogin } }
      ).then(() => {
        return Promise.resolve(this.removeReplacementValidator());
      }).catch(() => {
        return Promise.resolve(this.addReplacementValidator());
      });
    }
    return Promise.resolve(this.removeReplacementValidator());
  }

  addReplacementValidator (): void {
    this.fieldReplacementLoginAPI.addLocalValidator('email-already-exist-local-validator', () => {
      return [{
        message: MessageService.translate('newaccount.backend.error.email.already.used'),
        name: 'newaccount.backend.error.email.already.used',
        valid: false
      }];
    });
    this.fieldReplacementLoginAPI.validate();
  }

  removeReplacementValidator(): void {
    this.fieldReplacementLoginAPI.removeLocalValidator('email-already-exist-local-validator');
    this.fieldReplacementLoginAPI.removeValidator('emailValidator');
    this.fieldReplacementLoginAPI.validate();
  }

}
